import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Chip,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import { X, UploadSimple } from "@phosphor-icons/react";
import useStyles from "../Breakdown.style";
import {
  createArtifact,
  getSingleArtifact,
  uploadVerifiedArtifact,
} from "../../../../Services/artificatServices/artificatServices";
import { toast } from "react-hot-toast";
import imageCompression from "browser-image-compression";

interface CreateNewArtifactProps {
  handleClose: () => void;
  projectId: string;
  onCreateSuccess?: () => void;
}

const validationSchema = Yup.object({
  artifactName: Yup.string()
    .required("Artifact name is required")
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must not exceed 100 characters"),
  artifactType: Yup.string().required("Category is required"),
  keywords: Yup.array().min(1, "At least one keyword is required"),
  artifactImage: Yup.mixed().required("Image is required"),
});

export default function CreateNewArtifact({
  handleClose,
  projectId,
  onCreateSuccess,
}: CreateNewArtifactProps) {
  const classes = useStyles();
  const [keywordInput, setKeywordInput] = useState("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      artifactName: "",
      artifactType: "",
      keywords: [] as string[],
      artifactImage: null as File | null,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const imageFile = values.artifactImage;
        let compressedFile;

        if (imageFile) {
          console.log(
            "originalFile instanceof Blob",
            imageFile instanceof Blob
          ); // true
          console.log(`originalFile size ${imageFile.size / 1024 / 1024} MB`);
          const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: false,
          };
          compressedFile = await imageCompression(imageFile, options);
          console.log(
            "compressedFile instanceof Blob",
            compressedFile instanceof Blob
          ); // true
          console.log(
            `compressedFile size ${compressedFile.size / 1024 / 1024} MB`
          ); // smaller than maxSizeMB
        }
        const data = {
          artifactName: values.artifactName,
          artifactType: values.artifactType,
          keywords: values.keywords,
          artifactDescription: "",
          artifactFileName: values.artifactImage?.name || "",
        };
        const res = await createArtifact(projectId, data);
        if (res.status === 200 || res.status === 201) {
          toast.success("Artifact created successfully");
          // handleClose();
          // Get single artifact
          const { artifactId } = res.data?.data;
          const artifact = await getSingleArtifact(artifactId);
          const signedUrl = artifact.data?.signedUrl;
          await handleUploadArtifact(signedUrl, compressedFile as File);
          toast.success("Artifact uploaded successfully");
          await uploadVerifiedArtifact(artifactId);
          onCreateSuccess?.();
          handleClose();
        }
      } catch (error) {
        toast.error("Error creating artifact");
        console.log(error);
      }
    },
  });

  const handleUploadArtifact = async (signedUrl: string, file: File) => {
    try {
      const res = await fetch(signedUrl, {
        method: "PUT",
        body: file,
      });
      if (!res.ok) {
        throw new Error("Failed to upload artifact");
      }
      return res;
    } catch (error) {
      toast.error("Error uploading artifact");
      console.log(error);
    }
  };

  const handleAddKeyword = () => {
    const keyword = keywordInput.trim();
    if (keyword && !formik.values.keywords.includes(keyword)) {
      formik.setFieldValue("keywords", [...formik.values.keywords, keyword]);
      setKeywordInput("");
    }
  };

  const handleRemoveKeyword = (keywordToRemove: string) => {
    formik.setFieldValue(
      "keywords",
      formik.values.keywords.filter((k) => k !== keywordToRemove)
    );
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      formik.setFieldValue("artifactImage", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    formik.setFieldValue("artifactImage", null);
    setImagePreview(null);
  };

  return (
    <Box
      className={classes.selectGenreRoot}
      sx={{ maxWidth: "500px !important" }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
          Create New Artifact
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{ color: "#606479", padding: "4px" }}
        >
          <X size={24} />
        </IconButton>
      </Box>

      <form onSubmit={formik.handleSubmit}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box className={classes.inputLabelAndField}>
            <Typography className={classes.inputLabel}>Name *</Typography>
            <TextField
              name="artifactName"
              placeholder="Enter artifact name"
              className={classes.inputField}
              value={formik.values.artifactName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.artifactName &&
                Boolean(formik.errors.artifactName)
              }
              helperText={
                formik.touched.artifactName && formik.errors.artifactName
              }
              sx={{
                "& .MuiFormHelperText-root": {
                  color: "#ff4444",
                  marginLeft: 0,
                },
              }}
            />
          </Box>

          <Box className={classes.inputLabelAndField}>
            <Typography className={classes.inputLabel}>Category *</Typography>
            <Select
              name="artifactType"
              value={formik.values.artifactType}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              displayEmpty
              sx={{
                backgroundColor: "#252A38",
                color: "white",
                borderRadius: "5px",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiSelect-icon": { color: "#606479" },
              }}
              error={
                formik.touched.artifactType &&
                Boolean(formik.errors.artifactType)
              }
            >
              <MenuItem value="" disabled sx={{ backgroundColor: "#252A38" }}>
                <span style={{ color: "#606479" }}>Select category</span>
              </MenuItem>
              <MenuItem value="Character" sx={{ backgroundColor: "#252A38" }}>
                Character
              </MenuItem>
              <MenuItem value="Location" sx={{ backgroundColor: "#252A38" }}>
                Location
              </MenuItem>
              <MenuItem value="Costume" sx={{ backgroundColor: "#252A38" }}>
                Costume
              </MenuItem>
              <MenuItem value="Others" sx={{ backgroundColor: "#252A38" }}>
                Others
              </MenuItem>
            </Select>
            {formik.touched.artifactType && formik.errors.artifactType && (
              <Typography sx={{ color: "#ff4444", fontSize: "12px", mt: 0.5 }}>
                {formik.errors.artifactType}
              </Typography>
            )}
          </Box>

          <Box className={classes.inputLabelAndField}>
            <Typography className={classes.inputLabel}>Keywords *</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                placeholder="Add keyword"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddKeyword();
                  }
                }}
                className={classes.inputField}
                sx={{ flex: 1 }}
              />
              <Button
                onClick={handleAddKeyword}
                sx={{
                  backgroundColor: "#6C65D9",
                  color: "white",
                  "&:hover": { backgroundColor: "#5a54c7" },
                  minWidth: "80px",
                }}
              >
                Add
              </Button>
            </Box>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 1 }}>
              {formik.values.keywords.map((keyword) => (
                <Chip
                  key={keyword}
                  label={keyword}
                  onDelete={() => handleRemoveKeyword(keyword)}
                  sx={{
                    backgroundColor: "#252A38",
                    color: "white",
                    "& .MuiChip-deleteIcon": {
                      color: "#606479",
                      "&:hover": { color: "#ff4444" },
                    },
                  }}
                />
              ))}
            </Box>
            {formik.touched.keywords && formik.errors.keywords && (
              <Typography sx={{ color: "#ff4444", fontSize: "12px", mt: 0.5 }}>
                {formik.errors.keywords}
              </Typography>
            )}
          </Box>

          <Box className={classes.inputLabelAndField}>
            <Typography className={classes.inputLabel}>Image *</Typography>
            {imagePreview ? (
              <Box className={classes.imageViewerDiv}>
                <button
                  type="button"
                  className={classes.removeThumbnailIcon}
                  onClick={handleRemoveImage}
                >
                  <X size={16} />
                </button>
                <Box className={classes.imageContentDiv}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className={classes.projectThumbnail}
                  />
                </Box>
              </Box>
            ) : (
              <Box
                className={classes.cardContentDiv2}
                sx={{ position: "relative", cursor: "pointer" }}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className={classes.imageInput}
                  style={{ position: "absolute", inset: 0, zIndex: 1 }}
                />
                <Box sx={{ textAlign: "center", pointerEvents: "none" }}>
                  <UploadSimple size={32} color="#606479" />
                  <Typography
                    sx={{ color: "#606479", fontSize: "12px", mt: 1 }}
                  >
                    Click to upload image
                  </Typography>
                </Box>
              </Box>
            )}
            {formik.touched.artifactImage && formik.errors.artifactImage && (
              <Typography sx={{ color: "#ff4444", fontSize: "12px", mt: 0.5 }}>
                {formik.errors.artifactImage}
              </Typography>
            )}
          </Box>

          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}
          >
            <Button
              onClick={handleClose}
              sx={{
                color: "#606479",
                "&:hover": { backgroundColor: "#252A38" },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              sx={{
                backgroundColor: "#6C65D9",
                color: "white",
                "&:hover": { backgroundColor: "#5a54c7" },
                px: 3,
              }}
            >
              Create
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
}
